import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { adminPromotionPackagesApi } from '../../services/adminApi';
import toast from 'react-hot-toast';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useConfirmationModal, modalPresets } from '../../hooks/useConfirmationModal';

interface PromotionPackageDto {
  id: string;
  name: string;
  description: string;
  price: number;
  durationDays: number;
  rankingBoost: number;
  maxAdsCount: number;
  
  // Count-based promotional features
  topAdCount: number;
  highlightCount: number;
  urgentTagCount: number;
  bumpUpCount: number;
  homepageGalleryCount: number;
  
  // Keep boolean flags for backward compatibility
  includesTopAd: boolean;
  includesHighlight: boolean;
  includesUrgentTag: boolean;
  includesBumpUp: boolean;
  includesHomepageGallery: boolean;
  isActive: boolean;
  features: string[];
  iconEmoji: string;
  colorClass: string;
  isPopular: boolean;
}

interface CreatePromotionPackageDto {
  name: string;
  description: string;
  price: number;
  durationDays: number;
  rankingBoost: number;
  maxAdsCount: number;
  
  // Count-based promotional features
  topAdCount: number;
  highlightCount: number;
  urgentTagCount: number;
  bumpUpCount: number;
  homepageGalleryCount: number;
  
  // Keep boolean flags for enabling/disabling features
  includesTopAd: boolean;
  includesHighlight: boolean;
  includesUrgentTag: boolean;
  includesBumpUp: boolean;
  includesHomepageGallery: boolean;
  isActive: boolean;
}

interface UpdatePromotionPackageDto extends CreatePromotionPackageDto {}

const AdminPromotionPackagesPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<PromotionPackageDto | null>(null);
  const { isOpen, modalConfig, showModal, confirm, cancel } = useConfirmationModal();
  const [formData, setFormData] = useState<CreatePromotionPackageDto>({
    name: '',
    description: '',
    price: 0,
    durationDays: 30,
    rankingBoost: 0,
    maxAdsCount: 1,
    
    // Count-based promotional features
    topAdCount: 0,
    highlightCount: 0,
    urgentTagCount: 0,
    bumpUpCount: 0,
    homepageGalleryCount: 0,
    
    // Keep boolean flags for enabling/disabling features
    includesTopAd: false,
    includesHighlight: false,
    includesUrgentTag: false,
    includesBumpUp: false,
    includesHomepageGallery: false,
    isActive: true
  });

  const queryClient = useQueryClient();

  // Fetch promotion packages
  const { data: packages, isLoading } = useQuery({
    queryKey: ['admin-promotion-packages'],
    queryFn: () => adminPromotionPackagesApi.getAll()
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: adminPromotionPackagesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-promotion-packages'] });
      setIsCreateModalOpen(false);
      resetForm();
      toast.success('پلن با موفقیت ایجاد شد');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'خطا در ایجاد پلن';
      toast.error(errorMessage);
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePromotionPackageDto }) =>
      adminPromotionPackagesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-promotion-packages'] });
      setIsEditModalOpen(false);
      setSelectedPackage(null);
      resetForm();
      toast.success('پلن با موفقیت به‌روزرسانی شد');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'خطا در به‌روزرسانی پلن';
      toast.error(errorMessage);
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: adminPromotionPackagesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-promotion-packages'] });
      toast.success('پلن با موفقیت حذف شد');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'خطا در حذف پلن';
      toast.error(errorMessage);
    }
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: adminPromotionPackagesApi.toggleActive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-promotion-packages'] });
      toast.success('وضعیت پلن به‌روزرسانی شد');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'خطا در به‌روزرسانی وضعیت پلن';
      toast.error(errorMessage);
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: 0,
      durationDays: 30,
      rankingBoost: 0,
      maxAdsCount: 1,
      
      // Count-based promotional features
      topAdCount: 0,
      highlightCount: 0,
      urgentTagCount: 0,
      bumpUpCount: 0,
      homepageGalleryCount: 0,
      
      // Keep boolean flags for enabling/disabling features
      includesTopAd: false,
      includesHighlight: false,
      includesUrgentTag: false,
      includesBumpUp: false,
      includesHomepageGallery: false,
      isActive: true
    });
  };

  const handleCreate = () => {
    setIsCreateModalOpen(true);
    resetForm();
  };

  const handleEdit = (pkg: PromotionPackageDto) => {
    setSelectedPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description,
      price: pkg.price,
      durationDays: pkg.durationDays,
      rankingBoost: pkg.rankingBoost || 0,
      maxAdsCount: pkg.maxAdsCount || 1,
      
      // Count-based promotional features
      topAdCount: pkg.topAdCount || 0,
      highlightCount: pkg.highlightCount || 0,
      urgentTagCount: pkg.urgentTagCount || 0,
      bumpUpCount: pkg.bumpUpCount || 0,
      homepageGalleryCount: pkg.homepageGalleryCount || 0,
      
      // Keep boolean flags for enabling/disabling features
      includesTopAd: pkg.includesTopAd,
      includesHighlight: pkg.includesHighlight,
      includesUrgentTag: pkg.includesUrgentTag,
      includesBumpUp: pkg.includesBumpUp,
      includesHomepageGallery: pkg.includesHomepageGallery,
      isActive: pkg.isActive
    });
    setIsEditModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditModalOpen && selectedPackage) {
      updateMutation.mutate({ id: selectedPackage.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string) => {
    showModal(modalPresets.delete('این پلن', () => {
      deleteMutation.mutate(id);
    }));
  };

  const handleToggleActive = (id: string) => {
    toggleActiveMutation.mutate(id);
  };

  const formatPrice = (price: number) => {
    return price === 0 ? 'رایگان' : `${price.toFixed(2)} دلار`;
  };

  const getFeatureIcon = (feature: string) => {
    if (feature.includes('Featured') || feature.includes('ویژه')) return '⭐';
    if (feature.includes('Highlight') || feature.includes('برجسته')) return '🔥';
    if (feature.includes('Urgent') || feature.includes('فوری')) return '⚡';
    if (feature.includes('Top') || feature.includes('ویژه')) return '📌';
    if (feature.includes('Gallery') || feature.includes('گالری')) return '🖼️';
    if (feature.includes('Bump Up') || feature.includes('ارتقا')) return '📈';
    return '✓';
  };

  const translatePackageName = (name: string) => {
    const translations: { [key: string]: string } = {
      'Gold Package': 'پلن طلایی',
      'Premium Package': 'پلن ویژه',
      'Free Package': 'پلن رایگان',
      'Gold Plan': 'پلن طلایی',
      'Premium Plan': 'پلن ویژه',
      'Free Plan': 'پلن رایگان'
    };
    return translations[name.trim()] || name;
  };

  const translatePackageDescription = (description: string) => {
    const translations: { [key: string]: string } = {
      'Maximum visibility with all features': 'حداکثر نمایش با تمام ویژگی‌ها',
      'Featured ad with advanced features': 'آگهی ویژه با ویژگی‌های پیشرفته',
      'Standard ad with basic features': 'آگهی استاندارد با ویژگی‌های پایه'
    };
    return translations[description.trim()] || description;
  };

  const translateFeature = (feature: string) => {
    let trimmedFeature = feature.trim();
    const hasCheckmark = trimmedFeature.startsWith('✓');
    if (hasCheckmark) {
      trimmedFeature = trimmedFeature.substring(1).trim();
    }
    
    // Handle dynamic features like "30-day Display" or "day Display-30"
    if (trimmedFeature.includes('day Display') || trimmedFeature.match(/\d+-?day Display/i)) {
      const dayMatch = trimmedFeature.match(/(\d+)/);
      if (dayMatch) {
        const days = dayMatch[1];
        return `${hasCheckmark ? '✓ ' : ''}نمایش ${days} روزه`;
      }
    }
    
    const translations: { [key: string]: string } = {
      'Standard Ad': 'آگهی استاندارد',
      'Standard Search Position': 'موقعیت استاندارد در جستجو',
      'Featured Badge': 'نشان ویژه',
      'Highlight Ad': 'برجسته کردن آگهی',
      'Urgent Badge': 'نشان فوری',
      'Bump Up Ad': 'ارتقای آگهی',
      'Homepage Gallery Display': 'نمایش در گالری صفحه اصلی',
      '2x More Views': '۲ برابر بازدید بیشتر',
      '5x More Views': '۵ برابر بازدید بیشتر'
    };
    
    // Check for exact match first
    if (translations[trimmedFeature]) {
      return `${hasCheckmark ? '✓ ' : ''}${translations[trimmedFeature]}`;
    }
    
    // Check for partial matches (for features with counts like "Featured Badge (2 items)")
    for (const [english, persian] of Object.entries(translations)) {
      if (trimmedFeature.startsWith(english)) {
        let result = trimmedFeature.replace(english, persian);
        result = result.replace(/\((\d+)\s*items?\)/gi, '($1 مورد)');
        return `${hasCheckmark ? '✓ ' : ''}${result}`;
      }
    }
    
    // If already in Persian or no translation found, return as is with checkmark
    return hasCheckmark ? `✓ ${trimmedFeature}` : trimmedFeature;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" dir="rtl">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">مدیریت پلن‌ها</h1>
          <p className="text-gray-600 mt-1">مدیریت پلن‌های ترفیع آگهی</p>
        </div>
        <button
          onClick={handleCreate}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2 gap-2-reverse"
        >
          <PlusIcon className="h-5 w-5" />
          افزودن پلن جدید
        </button>
      </div>

      {/* Packages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {packages?.data?.map((pkg) => (
          <div
            key={pkg.id}
            className={`bg-white rounded-lg shadow-md border-2 ${
              pkg.isActive ? 'border-green-200' : 'border-gray-200'
            } hover:shadow-lg transition-shadow`}
          >
            <div className="p-6">
              {/* Package Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{pkg.iconEmoji}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{translatePackageName(pkg.name)}</h3>
                    <p className="text-sm text-gray-600">{translatePackageDescription(pkg.description)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleToggleActive(pkg.id)}
                    className={`p-1 rounded ${
                      pkg.isActive
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                    title={pkg.isActive ? 'غیرفعال کردن' : 'فعال کردن'}
                  >
                    {pkg.isActive ? (
                      <EyeIcon className="h-4 w-4" />
                    ) : (
                      <EyeSlashIcon className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(pkg)}
                    className="p-1 text-primary-600 hover:bg-primary-50 rounded"
                    title="ویرایش"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(pkg.id)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                    title="حذف"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Price */}
              <div className="text-center mb-4">
                <div className="text-3xl font-semibold text-primary-600">
                  {formatPrice(pkg.price)}
                </div>
                <div className="text-sm text-gray-500">
                  برای {pkg.durationDays} روز
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  حداکثر {pkg.maxAdsCount} آگهی
                </div>
              </div>

              {/* Features */}
              <div className="space-y-2">
                {pkg.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <span className="text-green-600">{getFeatureIcon(feature)}</span>
                    <span className="text-gray-700">{translateFeature(feature)}</span>
                  </div>
                ))}
              </div>

              {/* Status Badge */}
              <div className="mt-4 pt-4 border-t">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    pkg.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {pkg.isActive ? 'فعال' : 'غیرفعال'}
                </span>
                {pkg.isPopular && (
                  <span className="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    محبوب
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {(isCreateModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" dir="rtl">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              {isEditModalOpen ? 'ویرایش پلن' : 'افزودن پلن جدید'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    نام پلن *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder="مثال: پلن ویژه"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    قیمت (دلار) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  توضیحات *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={3}
                  required
                  placeholder="توضیحات مختصر درباره پلن"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    مدت زمان (روز) *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={formData.durationDays}
                    onChange={(e) => setFormData({ ...formData, durationDays: parseInt(e.target.value) || 30 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    حداکثر تعداد آگهی *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.maxAdsCount}
                    onChange={(e) => setFormData({ ...formData, maxAdsCount: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    تعداد آگهی‌هایی که کاربر می‌تواند با این پلن منتشر کند
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    امتیاز رتبه‌بندی پلن *
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1000"
                    value={formData.rankingBoost}
                    onChange={(e) => setFormData({ ...formData, rankingBoost: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    امتیاز خودکار برای تمام آگهی‌های این پلن (۰-۱۰۰۰)
                  </p>
                </div>
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  ویژگی‌های پلن
                </label>
                
                {/* Featured Badge */}
                <div className="border rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.includesTopAd}
                        onChange={(e) => setFormData({ ...formData, includesTopAd: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="mr-2 text-sm font-medium">نشان ویژه</span>
                    </label>
                  </div>
                  {formData.includesTopAd && (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">تعداد</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.topAdCount}
                        onChange={(e) => setFormData({ ...formData, topAdCount: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                  )}
                </div>

                {/* Highlight Ad */}
                <div className="border rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.includesHighlight}
                        onChange={(e) => setFormData({ ...formData, includesHighlight: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="mr-2 text-sm font-medium">برجسته کردن آگهی</span>
                    </label>
                  </div>
                  {formData.includesHighlight && (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">تعداد</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.highlightCount}
                        onChange={(e) => setFormData({ ...formData, highlightCount: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                  )}
                </div>

                {/* Urgent Badge */}
                <div className="border rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.includesUrgentTag}
                        onChange={(e) => setFormData({ ...formData, includesUrgentTag: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="mr-2 text-sm font-medium">نشان فوری</span>
                    </label>
                  </div>
                  {formData.includesUrgentTag && (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">تعداد</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.urgentTagCount}
                        onChange={(e) => setFormData({ ...formData, urgentTagCount: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                  )}
                </div>

                {/* Bump Up Ad */}
                <div className="border rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.includesBumpUp}
                        onChange={(e) => setFormData({ ...formData, includesBumpUp: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="mr-2 text-sm font-medium">ارتقای آگهی</span>
                    </label>
                  </div>
                  {formData.includesBumpUp && (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">تعداد</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.bumpUpCount}
                        onChange={(e) => setFormData({ ...formData, bumpUpCount: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        کاربر می‌تواند آگهی خود را در لیست ارتقا دهد
                      </p>
                    </div>
                  )}
                </div>

                {/* Show in Homepage Gallery */}
                <div className="border rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.includesHomepageGallery}
                        onChange={(e) => setFormData({ ...formData, includesHomepageGallery: e.target.checked })}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="mr-2 text-sm font-medium">نمایش در گالری صفحه اصلی</span>
                    </label>
                  </div>
                  {formData.includesHomepageGallery && (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">تعداد</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.homepageGalleryCount}
                        onChange={(e) => setFormData({ ...formData, homepageGalleryCount: parseInt(e.target.value) || 0 })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        placeholder="0"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="mr-2 text-sm font-medium">فعال</span>
                </label>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 gap-3-reverse pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setIsEditModalOpen(false);
                    setSelectedPackage(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  لغو
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'در حال پردازش...'
                    : isEditModalOpen
                    ? 'به‌روزرسانی'
                    : 'ایجاد'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={confirm}
        onClose={cancel}
        title={modalConfig?.title || ''}
        message={modalConfig?.message || ''}
        confirmText={modalConfig?.confirmText || 'Confirm'}
        cancelText={modalConfig?.cancelText || 'Cancel'}
        type={modalConfig?.type || 'warning'}
      />
    </div>
  );
};

export default AdminPromotionPackagesPage;
